import { Injectable } from "@angular/core";
import { awaitFor } from "../classes/misc";
import { BackendService } from "./common/backend.service";
import { HttpErrorHandlerService } from "./common/http-error-handler.service";

export type PracticePathCheckerWorkflowStatus = "RUNNING" | "COMPLETED" | "FAILED";

export type PracticePathCheckerWorkflowProgress = {
  totalCriticalPoints: number;
  processedCriticalPoints: number;
};

export type PracticePathCheckerCriticalPointCheck = {
  criticalPointUuid: string;
  isOk: boolean;
  reason?: string;
};

export type PracticePathCheckerWorkflowResult = {
  practicePathUuid: string;
  criticalPointChecks: PracticePathCheckerCriticalPointCheck[];
};

export type PracticePathCheckerWorkflowStartResponse = {
  workflowId: string;
  status: "RUNNING";
};

export type PracticePathCheckerWorkflowRunningResponse = {
  workflowId: string;
  status: "RUNNING";
  progress?: PracticePathCheckerWorkflowProgress;
};

export type PracticePathCheckerWorkflowCompletedResponse = {
  workflowId: string;
  status: "COMPLETED";
  result: PracticePathCheckerWorkflowResult;
};

export type PracticePathCheckerWorkflowFailedResponse = {
  workflowId: string;
  status: "FAILED";
  errorMessage?: string;
};

export type PracticePathCheckerWorkflowStateResponse =
  | PracticePathCheckerWorkflowRunningResponse
  | PracticePathCheckerWorkflowCompletedResponse
  | PracticePathCheckerWorkflowFailedResponse;

@Injectable({
  providedIn: "root",
})
export class PracticePathCriticalPointCheckerService {
  private readonly workflowsApi = "/api/admin/ai/practice-path-checker/workflows";

  constructor(
    private backendService: BackendService,
    private httpErrorHandlerService: HttpErrorHandlerService
  ) {}

  public async startWorkflow(
    practicePathUuid: string
  ): Promise<PracticePathCheckerWorkflowStartResponse> {
    try {
      return await this.backendService.callApi<
        { practicePathUuid: string },
        PracticePathCheckerWorkflowStartResponse
      >(this.workflowsApi, "POST", { practicePathUuid });
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(
        error,
        "Hiba az útvonal ellenőrzésének indításakor."
      );
      throw error;
    }
  }

  public async getWorkflowState(
    workflowId: string
  ): Promise<PracticePathCheckerWorkflowStateResponse> {
    try {
      return await this.backendService.callApi<
        undefined,
        PracticePathCheckerWorkflowStateResponse
      >(`${this.workflowsApi}/${workflowId}`, "GET");
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(
        error,
        "Hiba az útvonal ellenőrzési állapotának lekérése közben."
      );
      throw error;
    }
  }

  public async waitForWorkflowToFinish(
    workflowId: string,
    onProgress: (
      currentState: PracticePathCheckerWorkflowRunningResponse
    ) => void,
    pollIntervalMs: number = 2000,
    shouldContinue?: () => boolean
  ): Promise<
    | PracticePathCheckerWorkflowCompletedResponse
    | PracticePathCheckerWorkflowFailedResponse
  > {
    while (true) {
      if (shouldContinue != null && shouldContinue() === false) {
        throw new Error("WORKFLOW_POLLING_ABORTED");
      }

      const workflowState = await this.getWorkflowState(workflowId);
      switch (workflowState.status) {
        case "RUNNING":
          onProgress(workflowState);
          await awaitFor(pollIntervalMs);
          break;
        case "COMPLETED":
          return workflowState;
        case "FAILED":
          return workflowState;
        default:
          throw new Error(
            `Ismeretlen workflow állapot: ${
              (workflowState as { status?: string }).status
            }`
          );
      }
    }
  }
}
